import { collection, doc, getDoc, getDocs, query, runTransaction, where } from 'firebase/firestore';
import { db } from './firebase';
import { CreateInventoryMovementData, InventoryMovement, InventoryMovementType } from '../types/warehouse';

/**
 * Készletmozgás – egyetlen, közös, atomikus belépési pont.
 * Minden készletváltozás (bevételezés, kiadás, átmozgatás, korrekció, leltár, foglalás, munkalap, szállítólevél)
 * ezen megy át: mozgásnapló + készletsor + termék összkészlet egy tranzakcióban.
 */
/**
 * Készletsor (inventory doc) feloldása: meglévő sor (régi, véletlen id-vel) vagy determinisztikus új id.
 * A determinisztikus id (raktár__termék[__hely]) miatt két egyidejű első bevételezés nem hoz létre két sort.
 * A tényleges mennyiséget utána a tranzakcióban olvassuk (transaction.get) – így atomikus.
 */
export const resolveInventoryRef = async (orgIdX: string, productId: string, warehouseId: string, locationId?: string) => {
  const snap = await getDocs(query(
    collection(db, 'inventory'),
    where('organizationId', '==', orgIdX || '__none__'),
    where('productId', '==', productId),
    where('warehouseId', '==', warehouseId)
  ));
  const loc = locationId || '';
  const exact = snap.docs.find(d => (d.data().locationId || '') === loc);
  const fallback = !locationId ? snap.docs[0] : undefined;
  const hit = exact || fallback;
  return hit ? doc(db, 'inventory', hit.id) : doc(db, 'inventory', `${warehouseId}__${productId}${loc ? `__${loc}` : ''}`);
};

export async function applyInventoryMovement(
  data: CreateInventoryMovementData,
  actor: { uid: string; email?: string | null; organizationId?: string | null }
): Promise<InventoryMovement> {
  const currentUser = actor;
  const orgId = actor.organizationId || '';
  if (!currentUser?.uid) throw new Error('A felhasználó nincs bejelentkezve.');
  if (!(Number(data.quantity) >= 0)) throw new Error('Érvénytelen mennyiség.');

  // ---- Előkészítés (tranzakción kívül): szervezet, készletsor-hivatkozások ----
  const whSnap = await getDoc(doc(db, 'warehouses', data.warehouseId));
  if (!whSnap.exists()) throw new Error('A raktár nem található.');
  const movementOrgId = (whSnap.data() as any)?.organizationId || orgId || '';
  const inventoryRef = await resolveInventoryRef(movementOrgId, data.productId, data.warehouseId, data.locationId);
  let targetInventoryRef: ReturnType<typeof doc> | null = null;
  if (data.type === InventoryMovementType.STOCK_TRANSFER) {
    if (!data.targetWarehouseId) throw new Error('Átmozgatás esetén a célraktár megadása kötelező.');
    if (data.targetWarehouseId === data.warehouseId && (data.targetLocationId || '') === (data.locationId || '')) throw new Error('A forrás és a cél ugyanaz.');
    targetInventoryRef = await resolveInventoryRef(movementOrgId, data.productId, data.targetWarehouseId, data.targetLocationId);
  }

  return runTransaction(db, async (transaction) => {
    // ===== 1. OLVASÁS (minden a tranzakcióban) =====
    const productRef = doc(db, 'products', data.productId);
    const productDoc = await transaction.get(productRef);
    if (!productDoc.exists()) throw new Error('A termék nem található.');
    const productData = productDoc.data() as any;

    const warehouseDoc = await transaction.get(doc(db, 'warehouses', data.warehouseId));
    if (!warehouseDoc.exists()) throw new Error('A raktár nem található.');
    const userDoc = await transaction.get(doc(db, 'users', currentUser.uid));

    const invDoc = await transaction.get(inventoryRef);
    const currentQuantity = invDoc.exists() ? Number((invDoc.data() as any).quantity || 0) : 0;
    const currentReserved = invDoc.exists() ? Number((invDoc.data() as any).reservedQuantity || 0) : 0;

    let targetWarehouseDoc: any = null;
    let targetQuantity = 0;
    if (targetInventoryRef) {
      targetWarehouseDoc = await transaction.get(doc(db, 'warehouses', data.targetWarehouseId!));
      if (!targetWarehouseDoc.exists()) throw new Error('A célraktár nem található.');
      const tDoc = await transaction.get(targetInventoryRef);
      targetQuantity = tDoc.exists() ? Number((tDoc.data() as any).quantity || 0) : 0;
    }

    // ===== 2. SZÁMÍTÁS =====
    const u = userDoc.exists() ? (userDoc.data() as any) : {};
    const userName = u.displayName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || currentUser.email || 'Ismeretlen felhasználó';
    const userEmail = u.email || currentUser.email || '';
    const q = Number(data.quantity) || 0;

    let newQuantity = currentQuantity;
    let newReserved = currentReserved;
    switch (data.type) {
      case InventoryMovementType.STOCK_IN: newQuantity = currentQuantity + q; break;
      case InventoryMovementType.STOCK_OUT:
        if (currentQuantity < q) throw new Error(`Nincs elegendő készlet (${currentQuantity} ${productData.unit || ''} van, ${q} kellene).`);
        newQuantity = currentQuantity - q; break;
      case InventoryMovementType.STOCK_ADJUSTMENT:
      case InventoryMovementType.STOCK_INVENTORY:
        newQuantity = q; break; // abszolút érték: a számolt / helyes mennyiség
      case InventoryMovementType.STOCK_TRANSFER:
        if (currentQuantity < q) throw new Error(`Nincs elegendő készlet az átmozgatáshoz (${currentQuantity} van).`);
        newQuantity = currentQuantity - q; break;
      case InventoryMovementType.STOCK_RESERVATION:
        if (currentQuantity - currentReserved < q) throw new Error('Nincs elegendő szabad készlet a foglaláshoz.');
        newReserved = currentReserved + q; break;
      case InventoryMovementType.STOCK_RESERVATION_RELEASE:
        if (currentReserved < q) throw new Error('Nincs ennyi foglalt készlet.');
        newReserved = currentReserved - q; break;
    }
    const delta = data.type === InventoryMovementType.STOCK_TRANSFER ? 0 : newQuantity - currentQuantity;

    // ===== 3. ÍRÁS =====
    const movementRef = doc(collection(db, 'inventoryMovements'));
    const unitCost = data.unitCost != null ? Number(data.unitCost) : (productData.purchasePrice != null ? Number(productData.purchasePrice) : undefined);
    const movementData: InventoryMovement = {
      id: movementRef.id,
      organizationId: movementOrgId,
      productId: data.productId,
      productCode: productData.code || '',
      productName: productData.name || '',
      warehouseId: data.warehouseId,
      warehouseName: (warehouseDoc.data() as any)?.name || '',
      ...(data.targetWarehouseId && { targetWarehouseId: data.targetWarehouseId }),
      ...(targetWarehouseDoc && { targetWarehouseName: targetWarehouseDoc.data()?.name }),
      ...(data.locationId && { locationId: data.locationId }),
      ...(data.targetLocationId && { targetLocationId: data.targetLocationId }),
      quantity: q,
      unit: productData.unit || 'db',
      date: data.date || new Date(),
      type: data.type,
      ...(data.documentId && { documentId: data.documentId }),
      ...(data.documentType && { documentType: data.documentType }),
      ...(data.documentNumber && { documentNumber: data.documentNumber }),
      ...(data.reason && { reason: data.reason }),
      ...(data.customerId && { customerId: data.customerId }),
      ...(data.worksheetId && { worksheetId: data.worksheetId }),
      ...(data.stockCountId && { stockCountId: data.stockCountId }),
      ...(unitCost != null && !isNaN(unitCost) && { unitCost }),
      quantityBefore: currentQuantity,
      quantityAfter: newQuantity,
      ...(delta !== 0 && { delta }),
      userId: currentUser.uid,
      userName,
      userEmail,
      ...(data.note && { note: data.note }),
      ...(data.lotNumber && { lotNumber: data.lotNumber }),
      ...(data.expiryDate && { expiryDate: data.expiryDate }),
      ...(data.serialNumber && { serialNumber: data.serialNumber }),
      ...(data.attachments && data.attachments.length > 0 && { attachments: data.attachments }),
      isActive: true,
      createdAt: new Date()
    } as InventoryMovement;
    transaction.set(movementRef, movementData);

    const base = {
      organizationId: movementOrgId,
      productId: data.productId,
      productCode: productData.code || '',
      productName: productData.name || '',
      unit: productData.unit || 'db',
      lastUpdated: new Date(),
      updatedBy: currentUser.uid
    };
    transaction.set(inventoryRef, {
      ...base,
      warehouseId: data.warehouseId,
      warehouseName: (warehouseDoc.data() as any)?.name || '',
      ...(data.locationId && { locationId: data.locationId }),
      quantity: newQuantity,
      reservedQuantity: newReserved
    }, { merge: true });

    if (targetInventoryRef && targetWarehouseDoc) {
      transaction.set(targetInventoryRef, {
        ...base,
        warehouseId: data.targetWarehouseId,
        warehouseName: targetWarehouseDoc.data()?.name || '',
        ...(data.targetLocationId && { locationId: data.targetLocationId }),
        quantity: targetQuantity + q
      }, { merge: true });
    }

    // Termék összesített készlete: a tranzakcióban olvasott értékhez a változás (átmozgatásnál 0)
    if (delta !== 0) {
      const prevTotal = Number(productData.currentStock || 0);
      transaction.update(productRef, { currentStock: Math.round((prevTotal + delta) * 1000) / 1000, updatedAt: new Date() });
    }
    return movementData;
  });
}

